import { createContext, useContext, useState, useEffect } from 'react';
import * as XLSX from 'xlsx';

const WheelContext = createContext();

export function WheelProvider({ children }) {
  const [participants, setParticipants] = useState([]);
  const [participantsList, setParticipantsList] = useState([]);
  const [winners, setWinners] = useState(() => {
    const savedWinners = localStorage.getItem('winners');
    return savedWinners ? JSON.parse(savedWinners) : [];
  });
  const [isSharkMode, setIsSharkMode] = useState(false);

  const addParticipant = (name) => {
    // Verificar si el nombre ya existe (ignorando mayúsculas/minúsculas)
    const nameExists = participantsList.some(
      p => p.name.toLowerCase() === name.toLowerCase()
    );

    if (nameExists) {
      throw new Error('Este nombre ya existe en la lista');
    }

    const newParticipant = {
      id: Date.now(),
      name,
      active: true
    };
    setParticipantsList([...participantsList, newParticipant]);
  };

  const toggleParticipant = (id) => {
    setParticipantsList(participantsList.map(p => 
      p.id === id ? { ...p, active: !p.active } : p
    ));
  };

  const removeParticipant = (id) => {
    setParticipantsList(participantsList.filter(p => p.id !== id));
  };

  // Actualizar participants cuando cambia la lista
  useEffect(() => {
    setParticipants(participantsList.filter(p => p.active).map(p => p.name));
  }, [participantsList]);

  useEffect(() => {
    localStorage.setItem('winners', JSON.stringify(winners));
  }, [winners]);

  const addWinner = (winner) => {
    const newWinner = {
      name: winner,
      date: new Date().toISOString(),
      mode: isSharkMode ? 'shark' : 'normal'
    };
    setWinners([newWinner, ...winners]);
  };

  const clearParticipants = () => {
    setParticipantsList([]);
  };

  const clearWinners = () => {
    if (window.confirm('¿Estás seguro de que quieres eliminar todo el historial?')) {
      setWinners([]);
      localStorage.removeItem('winners');
    }
  };

  const exportWinnersToExcel = () => {
    const data = winners.map(winner => ({
      'Nombre': winner.name,
      'Fecha': new Date(winner.date).toLocaleString(),
      'Modo': winner.mode === 'shark' ? '🦈 Tiburón' : '🎲 Normal'
    }));

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(data);

    // Ajustar ancho de columnas
    const colWidths = [
      { wch: 30 }, // Nombre
      { wch: 25 }, // Fecha
      { wch: 15 }, // Modo
    ];
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Ganadores");
    XLSX.writeFile(wb, "historial_ganadores.xlsx");
  };

  const toggleSharkMode = () => {
    setIsSharkMode(prev => !prev);
  };

  const value = {
    participants,
    participantsList,
    addParticipant,
    toggleParticipant,
    removeParticipant,
    winners,
    addWinner,
    clearParticipants,
    clearWinners,
    exportWinnersToExcel,
    isSharkMode,
    toggleSharkMode,
  };

  return (
    <WheelContext.Provider value={value}>
      {children}
    </WheelContext.Provider>
  );
}

export const useWheel = () => {
  const context = useContext(WheelContext);
  if (!context) {
    throw new Error('useWheel must be used within a WheelProvider');
  }
  return context;
}; 